import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import { getAgeProvider } from '@/modules/auth/ageAssurance';
import { ROUTES } from '@/config/routes';

/**
 * B2 · Hard age-gate entry. Collects DOB, runs the configured assurance
 * provider, and on success persists ONLY the derived verified state (no raw
 * DOB — data minimisation). Gated surfaces redirect here until age-verified
 * (when the `age_gate` flag is on).
 */
export const VerifyAgePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await getAgeProvider().assure({ dob });
      if (!result.verified) {
        setError('You have to be 18+ to be here.');
        setBusy(false);
        return;
      }
      const supabase = getSupabase();
      if (supabase && user) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any).from('hottr_age_checks').upsert(
          {
            user_id: user.id,
            status: 'verified',
            method: result.method,
            verified_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' },
        );
      }
      navigate(ROUTES.feed, { replace: true });
    } catch {
      setError('Could not verify right now. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-5 bg-[#050608] px-6 text-center text-white">
      <div className="font-display text-2xl font-extrabold tracking-tight">
        HOTTR<span className="text-[#FF0CB6]">.</span>
      </div>
      <p className="max-w-[34ch] text-sm text-white/70">
        One quick check — confirm your date of birth. You have to be 18+.
      </p>
      <input
        type="date"
        aria-label="Date of birth"
        value={dob}
        onChange={(e) => setDob(e.target.value)}
        className="w-[240px] rounded-[2px] border border-white/20 bg-[#1A1A1A] px-3 py-2 text-white focus:border-[#FF0CB6] focus:outline-none"
      />
      {error && <p className="max-w-[34ch] text-sm text-[#FF0CB6]">{error}</p>}
      <button
        type="button"
        disabled={busy || !dob}
        onClick={submit}
        className="rounded-[2px] bg-[#FF0CB6] px-6 py-2 font-display text-sm uppercase tracking-wide text-white disabled:opacity-50"
      >
        {busy ? 'Checking…' : 'Confirm'}
      </button>
    </div>
  );
};
